import {
  createColumnHelper,
  flexRender,
  type CellContext,
  type PaginationState,
  type SortingState,
  useTable
} from '@tanstack/react-table'
import {
  ConfirmDeletionModal,
  ContractAnalysisRow,
  getSubstanceText,
  Mtds,
  paginatedTableFeatures,
  SubstanceTags,
  toLocaleDateString,
  useDeleteContractAnalysisTypeMutation,
  useErrorToast
} from 'prove-common'
import { useState, useMemo, Dispatch, SetStateAction } from 'react'
import { Link } from 'react-router'

type CombinationsTableProps = {
  data: ContractAnalysisRow[]
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const columnHelper = createColumnHelper<
  typeof paginatedTableFeatures,
  ContractAnalysisRow
>()

const getStatusTooltip = (
  status: ContractAnalysisRow['status'],
  approvalComment: string | undefined
) => {
  if (status === 'declined') {
    return approvalComment ?? 'Laboratoriet har avvist kombinasjonen.'
  }

  if (status === 'awaiting') {
    return 'Kombinasjonen avventer godkjenning hos laboratoriet.'
  }

  return undefined
}

export const StatusCell = ({
  status,
  approvalComment
}: {
  status: ContractAnalysisRow['status']
  approvalComment?: string
}) => {
  const meta: Record<
    ContractAnalysisRow['status'],
    { color: 'success' | 'danger' | 'warning'; label: string }
  > = {
    approved: { color: 'success', label: 'Godkjent' },
    declined: { color: 'danger', label: 'Avvist' },
    awaiting: { color: 'warning', label: 'Avventer' }
  }
  const { color, label } = meta[status]
  const tooltip = getStatusTooltip(status, approvalComment)
  return (
    <Mtds.Tag data-color={color} data-tooltip={tooltip}>
      {label}
    </Mtds.Tag>
  )
}

const getAnalysisTypeLabel = (row: ContractAnalysisRow): string => {
  const at = row.analysisType.analysisType
  const parts = [
    at?.sampleType?.product,
    at?.sampleType?.matrix,
    getSubstanceText(at),
    at?.payloadSchema?.name
  ].filter(Boolean)
  return parts.join(' / ') || `ID ${row.analysisType.id}`
}

const renderStatusCell = (
  info: CellContext<typeof paginatedTableFeatures, ContractAnalysisRow, unknown>
) => (
  <StatusCell
    status={info.row.original.status}
    approvalComment={info.row.original.analysisType.approvalComment}
  />
)

export const CombinationsTable = ({
  data,
  pagination,
  setPagination
}: CombinationsTableProps) => {
  const [deleteRow, setDeleteRow] = useState<ContractAnalysisRow | undefined>(
    undefined
  )
  const deleteContractAnalysisType = useDeleteContractAnalysisTypeMutation()
  const showError = useErrorToast('fjerne analysetypen fra kontrakten')
  const [sorting, setSorting] = useState<SortingState>([])

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: 'status',
          header: 'Godkjenning',
          cell: renderStatusCell
        }),
        columnHelper.display({
          id: 'produkt',
          header: 'Produkt',
          cell: (info) => {
            const sampleType =
              info.row.original.analysisType.analysisType?.sampleType
            return sampleType?.product ?? '-'
          }
        }),
        columnHelper.display({
          id: 'materiale',
          header: 'Prøve av',
          cell: (info) => {
            const sampleType =
              info.row.original.analysisType.analysisType?.sampleType
            return <strong>{sampleType?.matrix ?? '-'}</strong>
          }
        }),
        columnHelper.accessor('analysisType.analysisType.substances', {
          enableSorting: false,
          header: 'Analyser for',
          cell: (info) => (
            <SubstanceTags
              analysisType={info.row.original.analysisType.analysisType}
              showAllWhenFits={false}
            />
          )
        }),
        // 3. Schema
        columnHelper.accessor('analysisType.analysisType.payloadSchema', {
          enableSorting: false,
          header: 'Skjema',
          cell: (info) => {
            const value = info.getValue()
            const contractId = info.row.original.contractId
            const schemaName = value?.name ?? '-'
            const schemaId = value?.id
            return (
              <Link
                data-tooltip={'Gå til skjema'}
                to={`/contracts/${contractId}/schemas/${schemaId}`}
              >
                {schemaName}
              </Link>
            )
          }
        }),
        // Last changed — helps track recent approval activity
        columnHelper.accessor('analysisType.updatedTime', {
          id: 'sistEndret',
          header: 'Sist endret',
          cell: (info) => {
            const time = info.getValue()
            const dateStr = toLocaleDateString(time)
            return dateStr ?? '-'
          }
        }),
        // Delete action
        columnHelper.display({
          id: 'actions',
          // prettier-ignore
          cell: ({ row }) => ( // NOSONAR
          <Mtds.Flex data-justify='end'>
            <Mtds.Button
              data-color='danger'
              onClick={() => setDeleteRow(row.original)}
            >
              Slett
            </Mtds.Button>
          </Mtds.Flex>
        )
        })
      ]),
    []
  )
  const table = useTable({
    features: paginatedTableFeatures,
    data,
    columns,
    state: {
      sorting,
      pagination
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: false,
    autoResetAll: false
  })
  return (
    <>
      <figure>
        <Mtds.Table
          data-align='center'
          aria-label='Analysetyper som er lagt til i kontrakten'
        >
          <thead data-nowrap>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Mtds.Table.ThSortable
                    id={header.id}
                    key={header.id}
                    aria-sort={header.column.getIsSorted()}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Mtds.Table.ThSortable>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} id={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Mtds.Table>
      </figure>
      <Mtds.Flex data-align='center' data-justify='space-between'>
        <Mtds.Pagination
          current={table.state.pagination.pageIndex + 1}
          total={table.getPageCount()}
          show={table.state.pagination.pageSize}
          props={({ page }) => ({
            onClick: () => {
              globalThis.scrollTo({ top: 0 })
              table.setPageIndex(page - 1)
            }
          })}
        />
        <p>
          Viser {table.getRowModel().rows.length} av{' '}
          {table.getPrePaginatedRowModel().rows.length}
        </p>
        <Mtds.Field
          as='select'
          value={table.state.pagination.pageSize.toString() || '20'}
          onChange={({ target }) => table.setPageSize(Number(target.value))}
          options={[
            { value: '10', label: 'Vis 10 pr. side' },
            { value: '20', label: 'Vis 20 pr. side' },
            { value: '50', label: 'Vis 50 pr. side' },
            { value: '100', label: 'Vis 100 pr. side' },
            { value: '200', label: 'Vis 200 pr. side' }
          ]}
        />
      </Mtds.Flex>
      <ConfirmDeletionModal
        setOpen={() => setDeleteRow(undefined)}
        isOpen={!!deleteRow}
        onConfirm={async () => {
          if (deleteRow == null) {
            Mtds.toast.danger('Kunne ikke fjerne analysetypen fra kontrakten')
            return
          }
          try {
            await deleteContractAnalysisType.mutateAsync({
              contractId: deleteRow.contractId,
              analysisTypeId: deleteRow.analysisType.id,
              etag: deleteRow.contractEtag
            })
            Mtds.toast.success('Analysetypen er fjernet fra kontrakten')
          } catch (error) {
            showError(error)
          } finally {
            setDeleteRow(undefined)
          }
        }}
        title={`Kombinasjonen «${deleteRow ? getAnalysisTypeLabel(deleteRow) : ''}» vil bli permanent fjernet fra kontrakten.`}
        isPending={deleteContractAnalysisType.isPending}
        heading='Bekreft fjerning'
        confirmLabel='Fjern'
      />
    </>
  )
}
